import React, { useState, useEffect, useRef } from "react";
import { Card, Row, Col, Form, Button, Modal, Alert } from "react-bootstrap";
import Layout from "../../components/Layout";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import ToastFile from "../../utils/ToastFile";
import {
  getContractorDetails,
  updateContractorProfile,
  changeContractorPassword,
  uploadContractorImage,
  updateContractor,
} from "../../api/ContractorApi";
import { MdEdit, MdSave, MdCancel, MdCameraAlt } from "react-icons/md";
import { FaEye, FaEyeSlash, FaUser } from "react-icons/fa";
import "./Profile.css";
import { changePassword } from "../../api/authApi";
import { getTenantDetails, updateTenant } from "../../api/TenantApi";

const CompanyProfile = () => {
  const [tenant, setTenant] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const fileInputRef = useRef(null);
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem("user"));
  const tenantId = user?.tenant_id;

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm();


  useEffect(() => {
    if (tenantId) {
      fetchCompanyProfile();
    }
  }, [tenantId]);

  const fetchCompanyProfile = async () => {
    try {
      const response = await getTenantDetails(tenantId);
      if (response.status === 200) {
        setTenant(response.data.data);
        reset(response.data.data);
        setLoading(false);
      } else {
        ToastFile("Failed to fetch profile", "error");
        setLoading(false);
      }
    } catch (error) {
      console.error("Error fetching profile:", error);
      ToastFile("Error fetching profile", "error");
      setLoading(false);
    }
  };

  const handleImageSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        ToastFile("Image size should be less than 5MB", "error");
        return;
      }
      
      const allowedTypes = ["image/jpeg", "image/jpg", "image/png", "image/gif"];
      if (!allowedTypes.includes(file.type)) {
        ToastFile("Please select a valid image file (JPEG, PNG, GIF)", "error");
        return;
      }

      setSelectedImage(file);
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const uploadImage = async () => {
    if (!selectedImage) {
      ToastFile("Please select an image first", "error");
      return;
    }

    setIsUploading(true);
    const formData = new FormData();
    formData.append("image", selectedImage);

    try {
      const response = await uploadContractorImage(tenantId, formData);
      if (response.status === 200) {
        ToastFile("Profile image updated successfully", "success");
        setSelectedImage(null);
        setImagePreview(null);
        fetchCompanyProfile(); // Refresh profile data
      } else {
        ToastFile("Failed to upload image", "error");
      }
    } catch (error) {
      console.error("Error uploading image:", error);
      ToastFile("Error uploading image", "error");
    } finally {
      setIsUploading(false);
    }
  };

  const onProfileSubmit = async (data) => {
    try {
      const payload = {
        ...tenant,
        name: data.name,
        abn:data.abn,
        contact_no: data.contact_no,
        address: data.address,
        email: data.email,
        modifiedBy: user.contractor_id,
        modifiedAt: new Date(),
      };

      const response = await updateTenant(tenantId, payload)
      if (response.status === 200) {
        ToastFile("Profile updated successfully", "success");
        setIsEditing(false);
        fetchCompanyProfile();
      } else {
        ToastFile("Failed to update profile", "error");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      ToastFile("Error updating profile", "error");
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    reset(tenant);
  };

  if (loading) {
    return (
      <Layout>
        <div className="loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-3">
        <div className="profile-header d-flex justify-content-between align-items-center">
          <span
            style={{
              fontSize: "22px",
              fontWeight: 600,
              minWidth: "140px",
            }}
          >
      Company Profile
          </span>
        </div>
        <Row>
          {/* Profile Image Section */}
          <Col lg={4} md={12} className="mb-4">
            <Card className="profile-card text-center">
              <Card.Body>
                <div className="profile-image-container mb-3">
                  {imagePreview || tenant?.logoImage ? (
                    <img
                      src={imagePreview || tenant?.logoImage}
                      alt="Profile"
                      className="rounded-circle profile-image"
                      style={{
                        width: "150px",
                        height: "150px",
                        objectFit: "cover",
                        border: "3px solid #dee2e6",
                      }}
                    />
                  ) : (
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center mx-auto"
                      style={{
                        width: "150px",
                        height: "150px",
                        backgroundColor: "#f8f9fa",
                        border: "3px solid #dee2e6",
                      }}
                    >
                      <FaUser size={60} color="#6c757d" />
                    </div>
                  )}
                  
                  <Button
                    variant="primary"
                    size="sm"
                    className="position-absolute bottom-0 end-0 rounded-circle camera-button"
                    style={{ width: "40px", height: "40px" }}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <MdCameraAlt />
                  </Button>
                </div>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleImageSelect}
                  accept="image/*"
                  style={{ display: "none" }}
                />

                {selectedImage && (
                  <div className="upload-buttons">
                    <Button
                      variant="success"
                      size="sm"
                      onClick={uploadImage}
                      disabled={isUploading}
                      className="me-2"
                    >
                      {isUploading ? "Uploading..." : "Upload Image"}
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => {
                        setSelectedImage(null);
                        setImagePreview(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                )}

                <h5 className="mt-3 mb-1">{tenant?.name}</h5>
                <p className="text-muted mb-0">{tenant?.email}</p>
                <span className={`badge status-badge ${tenant?.status === "Active" ? "bg-success" : "bg-danger"}`}>
                  {tenant?.status}
                </span>
              </Card.Body>
            </Card>
          </Col>

          {/* Profile Details Section */}
          <Col lg={8} md={12}>
            <Card className="profile-info-card">
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Profile Information</h5>
                {!isEditing ? (
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => setIsEditing(true)}
                    className="edit-button"
                  >
                    <MdEdit className="me-1" />
                    Edit Profile
                  </Button>
                ) : (
                  <div>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={handleSubmit(onProfileSubmit)}
                      className="me-2"
                    >
                      <MdSave className="me-1" />
                      Save
                    </Button>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={handleCancelEdit}
                    >
                      <MdCancel className="me-1" />
                      Cancel
                    </Button>
                  </div>
                )}
              </Card.Header>
              <Card.Body>
                <Form>
                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Name</Form.Label>
                        <Form.Control
                          type="text"
                          {...register("name", { required: "Name is required" })}
                          disabled={!isEditing}
                          isInvalid={errors.name}
                        />
                        {errors.name && (
                          <Form.Control.Feedback type="invalid">
                            {errors.name.message}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>ABN</Form.Label>
                        <Form.Control
                           type="text"
                          {...register("abn")}
                          disabled={!isEditing}
                          isInvalid={errors.abn}
                        />
                        {errors.abn && (
                          <Form.Control.Feedback type="invalid">
                            {errors.abn.message}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          {...register("email", {
                            pattern: {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: "Invalid email address"
                            }
                          })}
                          disabled
                          isInvalid={errors.email}
                        />
                        {errors.email && (
                          <Form.Control.Feedback type="invalid">
                            {errors.email.message}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Contact Number</Form.Label>
                        <Form.Control
                          type="text"
                          {...register("contact_no")}
                          disabled={!isEditing}
                          isInvalid={errors.contact_no}
                        />
                        {errors.contact_no && (
                          <Form.Control.Feedback type="invalid">
                            {errors.contact_no.message}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                    </Col>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>Status</Form.Label>
                        <Form.Control
                          type="text"
                          value={tenant?.status}
                          disabled
                          className="bg-light"
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Form.Group className="mb-3">
                    <Form.Label>Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      {...register("address")}
                      disabled={!isEditing}
                      isInvalid={errors.address}
                    />
                    {errors.address && (
                      <Form.Control.Feedback type="invalid">
                        {errors.address.message}
                      </Form.Control.Feedback>
                    )}
                  </Form.Group>

                </Form>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>
    </Layout>
  );
};

export default CompanyProfile; 